import classnames from 'classnames';
import React, {useEffect, useRef} from 'react';
import * as PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import {NAV_MODES, PAM_WELCOME_LOGO} from 'Utils/constants';
import {prependDigitalAssetsUrl} from 'Utils/prependDigitalAssetsUrl';
import * as mapManager from 'Utils/explorer/mapManager';
import makeStyles from '@material-ui/core/styles/makeStyles';
import hexToRgb from 'Utils/hexToRGB';
import ClientSwitcher from 'Components/Explorer/common/ClientSwitcher';
import ExplorerModal from 'Components/Explorer/ExplorerModals/ExplorerModal';

const useStyles = makeStyles(() => ({
    content: {
        '& .modal-title': {
            color: props => `${props.textColor} !important`,
        },
        '& .modal-underscore': {
            visibility: 'hidden',
        },
        '& .modal-sub-title': {
            color: props => `${props.textColor} !important`,
            marginBottom: '4px !important',
        },
    },
    background: {
        background: props => hexToRgb(props.backgroundColor, 0.8),
        backdropFilter: 'blur(4px)',
        animation: '$backgroundFade 0.3s ease-out forwards',
    },
    '@keyframes backgroundFade': {
        from: {
            background: '#000000CC',
        },
        to: {
            background: props => hexToRgb(props.backgroundColor, 0.8),
        },
    },
    button: {
        height: 41,
        width: 262,
        marginBottom: 20,
    },
}));

const WelcomeModal = props => {
    const welcomeScreenColors = props.settings?.brandingConfig?.welcomeScreen?.colors;
    const textColor = welcomeScreenColors?.text ?? '#FFFFFF';
    const backgroundColor = welcomeScreenColors?.background ?? '#000000';

    const modalClasses = useStyles({textColor, backgroundColor});

    const imageUrl = props.welcomeImage;

    const requestRef = useRef();

    const animate = timestamp => {
        mapManager.rotate(timestamp);
        requestRef.current = requestAnimationFrame(animate);
    };

    useEffect(() => {
        requestRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(requestRef.current);
    }, []);

    // TODO: Refactor the code below as it is is very similar to ClientSwitcherModal

    if (!props.settings) {
        return null;
    }

    const selectedClientId = props.settings.clientId;
    const parentClientId = props.settings.parentClientId ?? props.settings.clientId;
    const parentClient = props.settings.clientList.find(client => client.id === parentClientId);
    const clientList = [
        {id: parentClientId, name: parentClient.name, navMapId: parentClient.navMapId},
        ...props.settings.clientList
            .sort((c1, c2) => c1.name.localeCompare(c2.name))
            .filter(client => client.id !== parentClientId),
    ];
    const isClientSwitcherVisible = props.settings.enableClientSwitching && clientList.length > 1;
    const clientSwitchingDescription = props.settings.clientSwitchingDescription;

    return (
        <div className={classnames('overlay', 'infoPanelOverlay', modalClasses.background)}>
            <ExplorerModal
                titleImage={
                    <img
                        alt={props.settings.projectName}
                        src={imageUrl ? prependDigitalAssetsUrl(imageUrl) : PAM_WELCOME_LOGO}
                        className="modal-branding-image"
                    />
                }
                // array to split text as required
                title={[
                    'WELCOME TO',
                    `${props.settings.projectName?.toUpperCase() || 'PAM'} EXPLORER MAP`,
                ]}
                subTitle={isClientSwitcherVisible ? clientSwitchingDescription : ''}
                buttons={
                    <div>
                        <ClientSwitcher
                            clientList={clientList}
                            selectedClientId={selectedClientId}
                            onClick={client => {
                                if (client.id !== selectedClientId) {
                                    window.location.href = `?navmapId=${client.navMapId}`;
                                }
                            }}
                            isVisible={isClientSwitcherVisible}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                className={classnames(modalClasses.button)}
                                onClick={() => {
                                    if ('geolocation' in navigator) {
                                        window.geolocateControl.trigger({welcomePageLoad: true});
                                    }

                                    props.navigate('explore', {});
                                    props.setNavMode(NAV_MODES.SEARCH);
                                    props.onClose();
                                }}
                                target="_blank"
                            >
                                START EXPLORING
                            </Button>
                        </ClientSwitcher>
                    </div>
                }
                onClose={() => {}}
                wrapperClass={modalClasses.content}
            />
        </div>
    );
};

WelcomeModal.propTypes = {
    navigate: PropTypes.func.isRequired,
    welcomeImage: PropTypes.string,
    setNavMode: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    settings: PropTypes.object,
};

export default WelcomeModal;
